import React, { useState, memo, useEffect, useCallback } from 'react'
import { colors } from '@pff-consumer/core-ui'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/browser-text'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { renderDisclaimerText } from '@pff-consumer/utils/lib/accounts/subscribe/util-functions'
import { CurrentProgressState, PaymentMethods, SubscriptionPlans } from '@pff-consumer/schema'
import { useStyles } from './credit-card-payment.styles'
import { CreditCardPaymentProps } from './credit-card-payment.props'
import { CreditCardForm } from './credit-card-form/credit-card-form.web'

/**
 * This Credit Card form uses Recurly for the credit card inputs and native inputs for the rest.
 * The native inputs need some work to be more accessible and easier to use.
 *
 *
 * INPUTS:
 * They currently:
 *  - Can be tabbed through
 *  - Have a focused indicator (blue border)
 *  - To have an invalid indicator and description (recurly error has the details we could source)
 *
 * They still need:
 *  - Localization/i15n support. All of the address fields are simply text inputs at the moment, no localization for field names for state vs region
 *    or country selector, etc. Not sure if the gateway prefers "US", "USA", or "United States" as another example
 *
 * We should consider an address auto complete form approach
 *  - Google: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete
 *  - Loqate: https://www.loqate.com/en-us/address-verification/address-capture/
 */

// These should probably come from Launch Darkly
const BUTTON_TEXT_ANNUAL_PLAN = 'Subscribe'
const BUTTON_TEXT_MONTHLY_PLAN = 'Subscribe'
const BUTTON_TEXT_LOADING = 'Processing Payment...'

const { useCheckoutActions } = checkoutHooks

export const CreditCardPayment: React.FC<CreditCardPaymentProps> = memo((props: CreditCardPaymentProps) => {
  const {
    isDisabled,
    isOnCompletePending,
    onComplete,
    onError,
    plan,
    price,
    onRecurlyTokenInit,
    isPaymentProcessing,
    isFormOpen,
  } = props
  const [isLocalPending, setIsLocalPending] = useState<boolean>(false)
  const { updatePaymentStatus } = useCheckoutActions()

  const isPending = isOnCompletePending || isLocalPending
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const isAnnualPlan = plan === SubscriptionPlans.PREMIUM_ANNUAL
  const submitButtonText = isAnnualPlan ? BUTTON_TEXT_ANNUAL_PLAN : BUTTON_TEXT_MONTHLY_PLAN

  const styles = useStyles({ isFormOpen })

  const formSubmitHandler = useCallback(() => {
    updatePaymentStatus(CurrentProgressState.IN_PROGRESS)

    onRecurlyTokenInit(PaymentMethods.CREDIT_CARD)

    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: submitButtonText,
      ctaLocation: 'Single Page Checkout',
    })
  }, [onRecurlyTokenInit, sendSubscribeAnalyticsEvent, submitButtonText, updatePaymentStatus])

  useEffect(() => {
    if (isPending) {
      setIsLocalPending(false)
    }
  }, [isPending])

  return (
    <>
      <CreditCardForm
        loadingText={BUTTON_TEXT_LOADING}
        submitText={submitButtonText}
        isFormOpen={isFormOpen}
        isPaymentProcessing={isPaymentProcessing}
        isPending={isPending}
        isDisabled={isDisabled}
        onSubmit={formSubmitHandler}
        onComplete={onComplete}
        onError={onError}
        enableAutoFocus={true}
      />

      {isFormOpen && (
        <div className={styles.disclaimer}>
          <Text
            variant={TextDescriptionVariant.XSMALL_PRIMARY_REGULAR}
            colorOverride={colors.subscriptionDescriptionText}
          >
            {renderDisclaimerText(price, submitButtonText, isAnnualPlan)}
          </Text>
        </div>
      )}
    </>
  )
})
